import { LoggerService, shareReplayRefCount } from '@adreno-common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { TeleworkerState } from '@app/core/models';
import { DiallerService } from '@app/dialler/services';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  merge,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  startWith,
  map,
  scan,
  take,
  switchMap,
  withLatestFrom,
  takeUntil,
  shareReplay,
} from 'rxjs/operators';
import { SubSink } from 'subsink';
import { WorkService } from '../services';

@Component({
  selector: 'app-outbound-waiting',
  templateUrl: './outbound-waiting.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-grow: 1;
        overflow: auto;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutboundWaitingComponent implements OnDestroy {
  private _stopProgress: boolean = false;
  private _stopProgress$ = new Subject<void>();
  private cancelClick$ = new BehaviorSubject<Event | undefined>(undefined);
  private personClick$ = new BehaviorSubject<Event | undefined>(undefined);
  private progress$: Observable<number>;
  private sink = new SubSink();
  private submitting = new BehaviorSubject<boolean>(false);
  private set stopProgress(v: boolean) {
    this._stopProgress = v;
    this._stopProgress$.next();
  }
  public get stopProgress(): boolean {
    return this._stopProgress;
  }
  private timer = { buffer: 3, delay: 6000, percentage: 4 };

  private voicemailClick$ = new BehaviorSubject<Event | undefined>(undefined);

  viewModel$: Observable<ViewModel>;
  constructor(
    private workSvc: WorkService,
    private diallerSvc: DiallerService,
    private logger: LoggerService
  ) {
    this.progress$ = this.workSvc.oncall$.pipe(
      withLatestFrom(this.workSvc.isFastVoicemail$),
      switchMap(([oncall, fastVoicemail]) => {
        if (oncall && fastVoicemail) {
          this.stopProgress = false;
          return interval(
            this.timer.delay / (100 / this.timer.percentage)
          ).pipe(
            startWith(-1),
            map((p) => (p + 1) * 4),
            take(100 / this.timer.percentage + this.timer.buffer + 1), //intnentionally just over 100%
            takeUntil(this._stopProgress$)
          );
        } else {
          return of(0);
        }
      }),
      shareReplayRefCount()
    );

    this.viewModel$ = this.initViewModel();

    this.sink.sink = combineLatest([
      this.workSvc.oncall$,
      this.progress$,
      this.cancelClick$,
      this.voicemailClick$,
      this.personClick$,
      this.workSvc.teleworkerState$,
    ])
      .pipe(withLatestFrom(this.workSvc.isFastVoicemail$))
      .subscribe(
        ([
          [oncall, progress, cancel, voicemail, person, teleworkerState],
          fastVoicemail,
        ]) => {
          if (!this.stopProgress) {
            if (cancel) {
              this.stopProgress = true;
              this.cancelClick$.next(undefined);
              this.diallerSvc.cancelGetNextNumber();
              this.workSvc.setCancelMessage();
              setTimeout(() => this.doClose(), 2000);
            } else if (teleworkerState === TeleworkerState.WrapOB) {
              this.stopProgress = true;
              this.personClick$.next(undefined);
              this.openRecord();
            } else if (oncall) {
              if (
                !fastVoicemail ||
                (fastVoicemail &&
                  progress >=
                    100 + this.timer.buffer * this.timer.percentage) ||
                person
              ) {
                this.stopProgress = true;
                this.personClick$.next(undefined);
                this.openRecord();
              } else if (voicemail) {
                this.stopProgress = true;
                this.voicemailClick$.next(undefined);
                this.submitting.next(true);
                this.diallerSvc
                  .hangUp()
                  .pipe(switchMap(() => this.diallerSvc.fastVoicemail()))
                  .subscribe(
                    (ok) => {
                      this.submitting.next(false);
                    },
                    (err) => this.submitting.next(false)
                  );
              }
            }
          }
        }
      );
  }

  private doClose() {
    this.diallerSvc.clearRetryEmptyQueue();
    this.diallerSvc.nailOutAndLogout();
    this.workSvc.showStateModal();
  }

  private initViewModel() {
    return merge<Partial<ViewModel>>(
      this.progress$.pipe(map((progress) => ({ progress }))),
      this.workSvc.obMessage$.pipe(map((message) => ({ message }))),
      this.workSvc.obContinue$.pipe(map((cont) => ({ continue: cont }))),
      this.workSvc.oncall$.pipe(map((oncall) => ({ oncall }))),
      this.workSvc.isFastVoicemail$.pipe(
        map((fastVoicemail) => ({ fastVoicemail }))
      ),
      this.submitting.pipe(map((submitting) => ({ submitting })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  cancel(e: Event) {
    this.cancelClick$.next(e);
  }

  continue() {
    this.diallerSvc.nailInAgain();
  }

  fastvoicemail(e: Event) {
    this.voicemailClick$.next(e);
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

  openRecord() {
    this.workSvc.popWaitingOBCall();
    // this.workSvc.navigateToScript();
  }

  person(e: Event) {
    this.personClick$.next(e);
  }
}

interface ViewModel {
  continue: boolean;
  fastVoicemail: boolean;
  message: string | undefined;
  oncall: boolean;
  progress: number;
  submitting: boolean;
}
